import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  Library,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { ApiError } from '@/api/client'
import {
  useCreateSavedPrompt,
  useDeleteSavedPrompt,
  useSavedPrompts,
  useUpdateSavedPrompt,
} from '@/api/queries'
import { useCurrentWorkspace } from '@/api/workspace-context-provider'
import {
  MAX_SAVED_PROMPT_CHARS,
  MAX_SAVED_PROMPT_TITLE_CHARS,
  estimateTokensFromChars,
  type SavedPromptRead,
} from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Textarea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PageContainer, PageBody, Section } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/_authenticated/prompts')({
  component: SavedPromptsPage,
})

function formatError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong'
}

interface DraftState {
  // ``null`` ⇒ not editing. ``''`` (empty string uuid) ⇒ composer for a brand
  // new prompt. Any other string ⇒ editing the prompt with that uuid.
  uuid: string | null
  name: string
  content: string
}

const EMPTY_DRAFT: DraftState = { uuid: null, name: '', content: '' }

export function SavedPromptsPage() {
  const { currentWorkspaceUuid } = useCurrentWorkspace()
  const { data: prompts, isLoading, error } = useSavedPrompts(currentWorkspaceUuid)
  const createPrompt = useCreateSavedPrompt(currentWorkspaceUuid ?? '')
  const updatePrompt = useUpdateSavedPrompt(currentWorkspaceUuid ?? '')
  const deletePrompt = useDeleteSavedPrompt(currentWorkspaceUuid ?? '')
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null)

  const promptCount = prompts?.length ?? 0

  const trimmedName = draft.name.trim()
  const trimmedContent = draft.content.trim()
  const nameTooLong = trimmedName.length > MAX_SAVED_PROMPT_TITLE_CHARS
  const contentTooLong = trimmedContent.length > MAX_SAVED_PROMPT_CHARS
  const valid =
    Boolean(currentWorkspaceUuid) &&
    trimmedName.length > 0 &&
    trimmedContent.length > 0 &&
    !nameTooLong &&
    !contentTooLong
  const submitting = createPrompt.isPending || updatePrompt.isPending
  const isComposingNew = draft.uuid === ''
  const isEditingExisting = draft.uuid !== null && draft.uuid !== ''

  const startCompose = () => setDraft({ uuid: '', name: '', content: '' })
  const startEdit = (prompt: SavedPromptRead) =>
    setDraft({
      uuid: prompt.uuid,
      name: prompt.name,
      content: prompt.content,
    })
  const cancelDraft = () => setDraft(EMPTY_DRAFT)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) return
    try {
      if (isEditingExisting) {
        await updatePrompt.mutateAsync({
          promptUuid: draft.uuid!,
          body: { name: trimmedName, content: trimmedContent },
        })
        toast.success(`Updated "${trimmedName}"`)
      } else {
        await createPrompt.mutateAsync({
          name: trimmedName,
          content: trimmedContent,
        })
        toast.success(`Saved "${trimmedName}"`)
      }
      setDraft(EMPTY_DRAFT)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const onDelete = async (prompt: SavedPromptRead) => {
    try {
      await deletePrompt.mutateAsync(prompt.uuid)
      toast.success(`Deleted "${prompt.name}"`)
      setConfirmingDelete(null)
      if (draft.uuid === prompt.uuid) setDraft(EMPTY_DRAFT)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Reusable prompts"
        title="Prompt library"
        description={
          <>
            Save fill-instruction snippets the team reaches for often (W-9
            quirks, licensing forms, signature blocks) and pick one when you
            kick off a fill. Prompts are workspace-scoped — every collaborator
            in this workspace sees and can use the same library.
          </>
        }
        actions={
          !isComposingNew && (
            <Button
              type="button"
              onClick={startCompose}
              data-testid="prompt-new"
            >
              <Plus className="size-4" aria-hidden />
              New prompt
            </Button>
          )
        }
      />
      <PageBody>
        {(isComposingNew || isEditingExisting) && (
          <Card data-testid="prompt-editor">
            <CardHeader>
              <CardTitle>
                {isEditingExisting ? 'Edit prompt' : 'New prompt'}
              </CardTitle>
              <CardDescription>
                {isEditingExisting
                  ? 'Changes apply to every future fill that picks this prompt.'
                  : 'Write the instruction you want to reuse. Keep it tight — the agent already has the source document and your saved workspace context.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="prompt-title">Name</Label>
                  <Input
                    id="prompt-title"
                    data-testid="prompt-title-input"
                    value={draft.name}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, name: e.target.value }))
                    }
                    placeholder="e.g. Tax W-9 dashes-only SSN"
                    maxLength={MAX_SAVED_PROMPT_TITLE_CHARS + 16}
                    disabled={submitting}
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="prompt-content">Instruction</Label>
                  <Textarea
                    id="prompt-content"
                    data-testid="prompt-content-input"
                    value={draft.content}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, content: e.target.value }))
                    }
                    rows={8}
                    placeholder="Write the instruction text the agent should follow when this prompt is selected."
                    className="resize-y"
                    disabled={submitting}
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span data-testid="prompt-content-charcount">
                      {trimmedContent.length.toLocaleString()} /{' '}
                      {MAX_SAVED_PROMPT_CHARS.toLocaleString()} chars · ~
                      {estimateTokensFromChars(
                        trimmedContent.length,
                      ).toLocaleString()}{' '}
                      tokens
                    </span>
                    {(contentTooLong || nameTooLong) && (
                      <span className="text-destructive">
                        {nameTooLong
                          ? `Name exceeds ${MAX_SAVED_PROMPT_TITLE_CHARS} chars`
                          : `Trim to ${MAX_SAVED_PROMPT_CHARS} chars`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancelDraft}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!valid || submitting}
                    data-testid="prompt-save"
                  >
                    {submitting && (
                      <Loader2 className="size-4 animate-spin" aria-hidden />
                    )}
                    {isEditingExisting ? 'Save changes' : 'Save prompt'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <Section data-testid="prompt-list-section">
          <h2 className="text-sm font-semibold text-foreground/80">
            Workspace prompts{' '}
            {promptCount > 0 && (
              <span className="font-normal text-muted-foreground">
                ({promptCount})
              </span>
            )}
          </h2>

          {isLoading && (
            <div className="grid gap-2" data-testid="prompt-loading">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}

          {error && !isLoading && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {formatError(error)}
            </p>
          )}

          {!isLoading && !error && promptCount === 0 && !isComposingNew && (
            <EmptyState
              icon={Library}
              title="No saved prompts yet"
              description="Saved prompts are reusable fill instructions shared with everyone in this workspace. Create one for the workflows the team does most often."
              action={
                <Button onClick={startCompose} data-testid="prompt-empty-new">
                  <Plus className="size-4" aria-hidden />
                  New prompt
                </Button>
              }
            />
          )}

          <ul className="grid gap-2" data-testid="prompt-list">
            {prompts?.map((prompt) => (
              <PromptCard
                key={prompt.uuid}
                prompt={prompt}
                editing={draft.uuid === prompt.uuid}
                confirmingDelete={confirmingDelete === prompt.uuid}
                onEdit={() => startEdit(prompt)}
                onRequestDelete={() => setConfirmingDelete(prompt.uuid)}
                onCancelDelete={() => setConfirmingDelete(null)}
                onConfirmDelete={() => onDelete(prompt)}
                deleting={deletePrompt.isPending}
              />
            ))}
          </ul>
        </Section>
      </PageBody>
    </PageContainer>
  )
}

interface PromptCardProps {
  prompt: SavedPromptRead
  editing: boolean
  confirmingDelete: boolean
  onEdit: () => void
  onRequestDelete: () => void
  onCancelDelete: () => void
  onConfirmDelete: () => void
  deleting: boolean
}

function PromptCard({
  prompt,
  editing,
  confirmingDelete,
  onEdit,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  deleting,
}: PromptCardProps) {
  const charCount = prompt.content.length
  const tokenEstimate = useMemo(
    () => estimateTokensFromChars(charCount),
    [charCount],
  )
  return (
    <li
      data-testid={`prompt-card-${prompt.uuid}`}
      className="rounded-lg border border-border bg-card px-3 py-3"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="truncate text-sm font-medium text-foreground">
              {prompt.name}
            </p>
            <Badge tone="neutral" size="xs">
              ~{tokenEstimate.toLocaleString()} tokens
            </Badge>
            {editing && (
              <Badge tone="primary" size="xs">
                Editing
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">
            {prompt.content}
          </p>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onEdit}
            data-testid={`prompt-edit-${prompt.uuid}`}
            aria-label={`Edit ${prompt.name}`}
          >
            <Pencil className="size-3.5" aria-hidden />
            Edit
          </Button>
          {!confirmingDelete ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRequestDelete}
              data-testid={`prompt-delete-${prompt.uuid}`}
              aria-label={`Delete ${prompt.name}`}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="size-3.5" aria-hidden />
              Delete
            </Button>
          ) : (
            <div
              className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-1.5 py-0.5 text-xs"
              role="alert"
              data-testid={`prompt-delete-confirm-${prompt.uuid}`}
            >
              <span className="text-destructive font-medium">Delete?</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancelDelete}
                aria-label="Cancel delete"
              >
                <X className="size-3.5" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onConfirmDelete}
                disabled={deleting}
                data-testid={`prompt-delete-confirm-yes-${prompt.uuid}`}
              >
                {deleting && (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                )}
                Yes
              </Button>
            </div>
          )}
        </div>
      </div>
    </li>
  )
}
